import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FiArrowLeft,
  FiCheck,
  FiX,
  FiBookOpen,
  FiClock,
  FiTarget,
  FiMessageCircle,
  FiHelpCircle,
  FiFileText,
  FiBarChart,
  FiBookmark,
  FiBook,
} from 'react-icons/fi';
import {
  FiChevronLeft,
  FiChevronRight,
  FiGrid,
  FiX as FiXIcon,
} from 'react-icons/fi';
import { InlineMath } from 'react-katex';

import 'katex/dist/katex.min.css';
import Sidebar from '../../Sidebar';
import './TAGELesson.css';
import GetStartedWith from '../../../components/GetStartedWith';
import { tageApi } from './tageApi';
import {
  getAccountScopedLocalStorageKey,
  isTokenValid,
  toastdisplay,
  logout,
} from '../../../utils/utils';
import { sanitizeHtml } from '../../../utils/safeHtml';
import { getErrorMessage } from '../../../utils/getErrorMessage';
import TAGEAnswerSheet from './TAGEAnswerSheet';
import DiagramRenderer from '../../../components/DiagramRenderer';

const getScopedLocalStorageItem = (key) => {
  const scopedKey = getAccountScopedLocalStorageKey(key);
  return scopedKey ? localStorage.getItem(scopedKey) : null;
};

const setScopedLocalStorageItem = (key, value) => {
  const scopedKey = getAccountScopedLocalStorageKey(key);
  if (!scopedKey) return false;
  localStorage.setItem(scopedKey, value);
  return true;
};

// Function to render math expressions in text using KaTeX
const renderMathText = (text) => {
  if (!text || !text.includes('§')) {
    return text;
  }

  const parts = text.split('§');

  return (
    <>
      {parts.map((part, index) => {
        if (index % 2 === 1) {
          try {
            return <InlineMath key={index} math={part} />;
          } catch (error) {
            console.warn('KaTeX parsing failed:', error);
            return <span key={index}>{part}</span>;
          }
        }
        return <span key={index}>{part}</span>;
      })}
    </>
  );
};

const TAGELesson = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentSection, setCurrentSection] = useState(0);
  const [showPractice, setShowPractice] = useState(false);
  const [practiceMode, setPracticeMode] = useState('timed');
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [practiceStartTime, setPracticeStartTime] = useState(null);
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const [isSavedToNotebook, setIsSavedToNotebook] = useState(false);
  const [showNotebookConfirmation, setShowNotebookConfirmation] =
    useState(false);
  const [showQuestionPalette, setShowQuestionPalette] = useState(false);
  const contentRef = useRef(null);
  const loadingLessonRef = useRef(false);
  const [toast, setToast] = useState({
    show: false,
    message: '',
    success: true,
  });

  useEffect(() => {
    if (!loadingLessonRef.current) {
      loadLesson();
    }
  }, [lessonId]);

  // Inject interactive components and images into lesson content
  useEffect(() => {
    // Skip if lesson is not loaded yet
    if (!lesson || loading) return;

    // Use setTimeout to ensure DOM is fully rendered before injecting
    const timer = setTimeout(() => {
      if (contentRef.current) {
        // Handle component injection
        const componentPlaceholders = contentRef.current.querySelectorAll(
          '[data-component]:not([data-component-processed])'
        );
        componentPlaceholders.forEach((placeholder) => {
          const componentName = placeholder.getAttribute('data-component');
          const propsString = placeholder.getAttribute('data-props');

          if (componentName === 'TAGEAnswerSheet') {
            try {
              placeholder.setAttribute('data-component-processed', 'true');
              const props = propsString ? JSON.parse(propsString) : {};
              const root = require('react-dom/client').createRoot(placeholder);
              root.render(<TAGEAnswerSheet {...props} />);
            } catch (error) {
              console.error('Error rendering TAGEAnswerSheet:', error);
            }
          } else if (componentName === 'Mafs') {
            try {
              placeholder.setAttribute('data-component-processed', 'true');

              // Parse props for DiagramRenderer
              let diagramProps = {};
              if (propsString) {
                try {
                  // Extract height
                  const heightMatch = propsString.match(/"height":\s*(\d+)/);
                  if (heightMatch) {
                    diagramProps.height = parseInt(heightMatch[1]);
                  }

                  // Extract viewBox
                  const viewBoxMatch = propsString.match(
                    /"viewBox":\s*(\{[^}]+\})/
                  );
                  if (viewBoxMatch) {
                    try {
                      diagramProps.viewBox = JSON.parse(viewBoxMatch[1]);
                    } catch (e) {
                      console.warn('Could not parse viewBox:', e);
                    }
                  }

                  // Extract code - handle escaped characters properly
                  const codeStart = propsString.indexOf('"code":');
                  if (codeStart !== -1) {
                    const afterCode = propsString
                      .substring(codeStart + 7)
                      .trim();
                    const quoteStart = afterCode.indexOf('"');
                    if (quoteStart !== -1) {
                      let quoteEnd = quoteStart + 1;
                      let escaped = false;
                      while (quoteEnd < afterCode.length) {
                        if (afterCode[quoteEnd] === '\\' && !escaped) {
                          escaped = true;
                        } else if (afterCode[quoteEnd] === '"' && !escaped) {
                          break;
                        } else {
                          escaped = false;
                        }
                        quoteEnd++;
                      }
                      diagramProps.code = afterCode.substring(
                        quoteStart + 1,
                        quoteEnd
                      );
                      // Unescape the string
                      diagramProps.code = diagramProps.code
                        .replace(/\\"/g, '"')
                        .replace(/\\\\/g, '\\')
                        .replace(/\\n/g, '\n')
                        .replace(/\\t/g, '\t');
                    }
                  }
                } catch (parseError) {
                  console.error('Error parsing diagram props:', parseError);
                }
              }

              // Render using DiagramRenderer component
              const root = require('react-dom/client').createRoot(placeholder);
              root.render(<DiagramRenderer {...diagramProps} />);
            } catch (error) {
              console.error('Error rendering Mafs:', error);
              placeholder.innerHTML = `<div style="color: red; padding: 1rem;">Erreur de rendu du graphique: ${error.message}</div>`;
            }
          }
        });

        // Handle image injection
        const imagePlaceholders = contentRef.current.querySelectorAll(
          '[data-image]:not([data-image-processed])'
        );
        imagePlaceholders.forEach((placeholder) => {
          const imageName = placeholder.getAttribute('data-image');
          const altText = placeholder.getAttribute('data-alt') || '';

          if (imageName) {
            placeholder.setAttribute('data-image-processed', 'true');
            try {
              // Show loading spinner
              const loader = document.createElement('div');
              loader.style.cssText = `
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              padding: 40px;
              gap: 15px;
            `;

              const spinner = document.createElement('div');
              spinner.style.cssText = `
              width: 50px;
              height: 50px;
              border: 4px solid #e0e0e0;
              border-top: 4px solid #1976d2;
              border-radius: 50%;
              animation: spin 1s linear infinite;
            `;

              const loadingText = document.createElement('p');
              loadingText.textContent = "Chargement de l'image...";
              loadingText.style.cssText = `
              color: #666;
              font-size: 14px;
              margin: 0;
            `;

              loader.appendChild(spinner);
              loader.appendChild(loadingText);
              placeholder.innerHTML = '';
              placeholder.appendChild(loader);

              // Add keyframe animation for spinner
              if (!document.getElementById('spin-animation')) {
                const style = document.createElement('style');
                style.id = 'spin-animation';
                style.textContent = `
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `;
                document.head.appendChild(style);
              }

              // Import the image dynamically
              const img = document.createElement('img');
              img.style.cssText = `
              max-width: 100%;
              height: auto;
              border: 2px solid #1976d2;
              border-radius: 8px;
              box-shadow: 0 4px 8px rgba(0,0,0,0.1);
              opacity: 0;
              transition: opacity 0.3s ease-in;
            `;
              img.alt = altText;

              // Handle image load
              img.onload = () => {
                placeholder.innerHTML = '';
                img.style.opacity = '1';
                placeholder.appendChild(img);
              };

              // Handle image error
              img.onerror = () => {
                placeholder.innerHTML = `
                <div style="
                  background-color: #ffebee;
                  padding: 20px;
                  border-radius: 8px;
                  border: 2px solid #f44336;
                  text-align: center;
                ">
                  <p style="color: #f44336; margin: 0;">
                    ⚠️ Impossible de charger l'image: ${imageName}
                  </p>
                </div>
              `;
              };

              // Set src to start loading
              img.src = require(`./img/${imageName}`);
            } catch (error) {
              console.error(`Failed to load image: ${imageName}`, error);
              placeholder.innerHTML = `
              <div style="
                background-color: #ffebee;
                padding: 20px;
                border-radius: 8px;
                border: 2px solid #f44336;
                text-align: center;
              ">
                <p style="color: #f44336; margin: 0;">
                  ⚠️ Image non disponible: ${imageName}
                </p>
              </div>
            `;
            }
          }
        });

        // Handle math expression rendering
        const processNode = (node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Skip interactive elements
            const tagName = node.tagName.toLowerCase();
            if (
              [
                'button',
                'input',
                'select',
                'textarea',
                'a',
                'label',
                'script',
                'style',
              ].includes(tagName)
            ) {
              return;
            }
            // Recurse on a static copy of child nodes
            Array.from(node.childNodes).forEach(processNode);
          } else if (node.nodeType === Node.TEXT_NODE) {
            const text = node.textContent;
            if (
              text &&
              (text.includes('§') ||
                /[\^²π√∞≤≥≠±∑∏∫∂∆]/.test(text) ||
                (/\//.test(text) && /\d/.test(text)) ||
                /^[a-zA-Z]\d$/.test(text) ||
                /^\d+[a-zA-Z]$/.test(text))
            ) {
              const parent = node.parentNode;
              if (parent) {
                const span = document.createElement('span');
                parent.insertBefore(span, node);
                parent.removeChild(node);

                try {
                  const root = require('react-dom/client').createRoot(span);
                  const mathRendered = renderMathText(text);
                  root.render(mathRendered);
                } catch (error) {
                  console.warn('Error rendering math in text node:', error);
                  // Restore original text if rendering fails
                  parent.insertBefore(document.createTextNode(text), span);
                  parent.removeChild(span);
                }
              }
            }
          }
        };

        // Start processing from the content root
        if (contentRef.current) {
          processNode(contentRef.current);
        }
      }
    }, 300); // 300ms delay to ensure DOM is fully rendered

    // Cleanup timer on unmount or dependency change
    return () => clearTimeout(timer);
  }, [currentSection, lesson, loading]);

  // Helper function to sync progress to backend (non-blocking)
  const syncProgressToBackend = async (lessonId, localProgress, lesson) => {
    try {
      const progressData = {
        lesson_id: lessonId,
        status: localProgress.status,
        current_exercise_index: localProgress.currentExercise || 0,
        exercises_completed: localProgress.completedExercises || 0,
        exercises_correct:
          localProgress.exerciseResults?.filter((r) => r.isCorrect).length || 0,
        total_exercises:
          localProgress.totalExercises || lesson?.exercises?.length || 0,
        exercise_results: localProgress.exerciseResults || [],
        total_time_seconds: 0, // Can be enhanced with actual timing
      };

      await tageApi.saveLessonProgress(progressData);
      console.log('✅ Progress synced to backend');

      // Dispatch event to invalidate progress cache
      window.dispatchEvent(
        new CustomEvent('tageProgressSaved', {
          detail: { lessonId: parseInt(lessonId, 10) },
        })
      );
    } catch (error) {
      console.warn(
        '⚠️ Failed to sync progress to backend (will retry later):',
        error
      );
    }
  };

  const loadLesson = async () => {
    // Prevent duplicate simultaneous calls
    if (loadingLessonRef.current) {
      console.log('⏸️ Already loading lesson, skipping duplicate request');
      return;
    }

    loadingLessonRef.current = true;
    setLoading(true);
    setError(null);

    try {
      // Check authentication
      if (!isTokenValid()) {
        toastdisplay(
          'Vous devez être connecté pour accéder aux leçons. Redirection vers la connexion dans 3 secondes...',
          false,
          3000,
          setToast
        );
        setLoading(false);
        loadingLessonRef.current = false;

        // Logout and redirect to login after delay
        setTimeout(async () => {
          await logout();
        }, 3000);
        return;
      }

      // Step 1: Try to load lesson from localStorage first
      console.log('💾 Checking localStorage for lesson:', lessonId);
      const cachedLesson = localStorage.getItem(`tage_lesson_${lessonId}`);
      let foundLesson = null;

      if (cachedLesson) {
        try {
          foundLesson = JSON.parse(cachedLesson);
          console.log('✅ Lesson loaded from localStorage:', foundLesson);
          setLesson(foundLesson);
        } catch (parseError) {
          console.warn(
            '⚠️ Failed to parse cached lesson, will fetch from backend'
          );
        }
      }

      // Step 2: If not in localStorage, fetch from backend
      if (!foundLesson) {
        console.log('📡 Fetching lesson from backend:', lessonId);
        const lessonResponse = await tageApi.getLesson(lessonId);

        if (!lessonResponse.success || !lessonResponse.lesson) {
          throw new Error('Leçon non trouvée');
        }

        // Adapt backend format to match local JSON format
        const backendLesson = lessonResponse.lesson;
        foundLesson = {
          id: backendLesson.lesson_code || backendLesson.id,
          title: backendLesson.title,
          description: backendLesson.description,
          type: backendLesson.tags?.type || backendLesson.section,
          work_time_hours: backendLesson.estimated_duration_minutes / 60 || 0,
          introduction: backendLesson.introduction || '',
          subcontent: backendLesson.theory_content || [],
          exercises: backendLesson.exercises || [],
        };

        // Cache lesson in localStorage
        localStorage.setItem(
          `tage_lesson_${lessonId}`,
          JSON.stringify(foundLesson)
        );
        console.log('✅ Lesson loaded from backend and cached:', foundLesson);
        setLesson(foundLesson);
      }

      // Step 3: Load progress (localStorage-first)
      console.log('💾 Checking localStorage for progress:', lessonId);
      const cachedProgress = getScopedLocalStorageItem(
        `tage_lesson_progress_${lessonId}`
      );

      if (cachedProgress) {
        try {
          const progress = JSON.parse(cachedProgress);
          console.log('✅ Progress loaded from localStorage:', progress);

          // Sync to backend in background (non-blocking)
          syncProgressToBackend(lessonId, progress, foundLesson);
        } catch (parseError) {
          console.warn('⚠️ Failed to parse cached progress');
        }
      } else {
        // No localStorage progress, try backend
        try {
          console.log('📡 Fetching progress from backend:', lessonId);
          const progressResponse = await tageApi.getLessonProgress(lessonId);

          if (progressResponse.success && progressResponse.progress) {
            // Adapt backend progress to localStorage format
            const backendProgress = progressResponse.progress;
            const localProgress = {
              status: backendProgress.status,
              currentExercise: backendProgress.current_exercise_index || 0,
              exerciseResults: backendProgress.exercise_results || [],
              completedExercises: backendProgress.exercises_completed || 0,
              totalExercises:
                backendProgress.total_exercises ||
                foundLesson.exercises?.length ||
                0,
              lastUpdated:
                backendProgress.updated_at || new Date().toISOString(),
            };

            // Cache in localStorage
            setScopedLocalStorageItem(
              `tage_lesson_progress_${lessonId}`,
              JSON.stringify(localProgress)
            );
            console.log('✅ Progress loaded from backend and cached');
          } else {
            // No progress exists, create initial progress
            const initialProgress = {
              status: 'in_progress',
              currentExercise: 0,
              exerciseResults: [],
              completedExercises: 0,
              totalExercises: foundLesson.exercises?.length || 0,
              lastUpdated: new Date().toISOString(),
            };

            // Save to localStorage first
            setScopedLocalStorageItem(
              `tage_lesson_progress_${lessonId}`,
              JSON.stringify(initialProgress)
            );

            // Then sync to backend
            syncProgressToBackend(lessonId, initialProgress, foundLesson);
          }
        } catch (progressError) {
          console.error('Error loading progress from backend:', progressError);
          // Create initial progress in localStorage
          const initialProgress = {
            status: 'in_progress',
            currentExercise: 0,
            exerciseResults: [],
            completedExercises: 0,
            totalExercises: foundLesson.exercises?.length || 0,
            lastUpdated: new Date().toISOString(),
          };
          setScopedLocalStorageItem(
            `tage_lesson_progress_${lessonId}`,
            JSON.stringify(initialProgress)
          );
        }
      }

      // Check if this lesson is already saved in notebook
      checkIfSavedInNotebook();
    } catch (error) {
      console.error('❌ Error loading lesson:', error);

      // Display appropriate toast based on error type
      let errorMessage = 'Impossible de charger la leçon. Veuillez réessayer.';

      if (error.message === 'AUTH_REQUIRED') {
        errorMessage = 'Vous devez être connecté pour accéder aux leçons.';
      } else if (error.message === 'SESSION_EXPIRED') {
        errorMessage = 'Session expirée. Veuillez vous reconnecter.';
      } else if (error.message === 'NOT_FOUND') {
        errorMessage = 'Leçon non trouvée.';
      } else if (error.message === 'NETWORK_ERROR') {
        errorMessage =
          'Erreur de connexion. Vérifiez votre connexion internet.';
      }

      toastdisplay(errorMessage, false, 3000, setToast);
      setTimeout(async () => {
        await logout();
      }, 3000);
      setError(errorMessage);
    } finally {
      setLoading(false);
      loadingLessonRef.current = false;
    }
  };

  // Check if lesson recap is already saved
  const checkIfSavedInNotebook = () => {
    const notebook = JSON.parse(
      getScopedLocalStorageItem('tage_notebook') || '[]'
    );
    const isSaved = notebook.some((note) => note.lessonId === lessonId);
    setIsSavedToNotebook(isSaved);
  };

  // Save recap to notebook
  const saveToNotebook = () => {
    if (!lesson) return;

    const notebook = JSON.parse(
      getScopedLocalStorageItem('tage_notebook') || '[]'
    );

    // Check if already saved
    if (notebook.some((note) => note.lessonId === lessonId)) {
      return;
    }

    const allLessonsHtml = lesson.subcontent
      ? lesson.subcontent.map((item) => item.lesson).join('')
      : '';
    const keyConcepts = extractKeyConcepts(allLessonsHtml);

    const newNote = {
      id: `note-${Date.now()}`,
      lessonId: lessonId,
      lessonTitle: lesson.title,
      lessonType: lesson.type,
      concepts: keyConcepts,
      savedAt: new Date().toISOString(),
    };

    notebook.push(newNote);
    setScopedLocalStorageItem('tage_notebook', JSON.stringify(notebook));
    setIsSavedToNotebook(true);
    setShowNotebookConfirmation(true);

    // Hide confirmation after 3 seconds
    setTimeout(() => {
      setShowNotebookConfirmation(false);
    }, 3000);
  };

  // Remove from notebook
  const removeFromNotebook = () => {
    const notebook = JSON.parse(
      getScopedLocalStorageItem('tage_notebook') || '[]'
    );

    const updatedNotebook = notebook.filter(
      (note) => note.lessonId !== lessonId
    );

    setScopedLocalStorageItem(
      'tage_notebook',
      JSON.stringify(updatedNotebook)
    );
    setIsSavedToNotebook(false);
  };

  // Timer functionality
  useEffect(() => {
    let interval = null;
    if (isTimerActive && timeLeft > 0) {
      interval = setInterval(() => setTimeLeft(timeLeft - 1), 1000);
    } else if (timeLeft === 0 && isTimerActive) {
      handleTimeUp();
    }
    return () => clearInterval(interval);
  }, [isTimerActive, timeLeft]);

  const handleTimeUp = () => {
    setIsTimerActive(false);
    const questions = getFilteredQuestions();
    if (currentQuestion < questions.length - 1) {
      setTimeout(() => {
        setCurrentQuestion(currentQuestion + 1);
        startTimer();
      }, 1000);
    } else {
      finishPractice();
    }
  };

  const startTimer = () => {
    if (practiceMode === 'timed' && lesson) {
      // Each question gets 2 minutes (120 seconds)
      setTimeLeft(120);
      setIsTimerActive(true);
    }
  };

  // Helper function to adapt backend exercise format to local format
  const adaptExerciseFormat = (exercise) => {
    // If options is an array, convert to object with optionA, optionB, etc.
    if (Array.isArray(exercise.options)) {
      const adaptedExercise = { ...exercise };
      exercise.options.forEach((option, index) => {
        const letter = String.fromCharCode(65 + index); // A, B, C, D, E
        adaptedExercise[`option${letter}`] = option.replace(/^[A-E]\)\s*/, ''); // Remove letter prefix if present
      });
      // Keep options array for rendering (don't delete it)
      // delete adaptedExercise.options;
      return adaptedExercise;
    }
    return exercise;
  };

  const getFilteredQuestions = () => {
    if (!lesson || !lesson.exercises) return [];
    return lesson.exercises.map((ex, index) => {
      const adapted = adaptExerciseFormat(ex);

      // Handle correct answer - convert letter to index if needed
      let correctIndex = adapted.correct || 0;
      if (
        typeof ex.correct_answer === 'string' &&
        ex.correct_answer.length === 1
      ) {
        // Convert letter to index (A=0, B=1, C=2, D=3, E=4)
        correctIndex = ex.correct_answer.charCodeAt(0) - 65;
      }

      return {
        ...adapted,
        type: practiceMode,
        timeLimit: 120, // 2 minutes per question
        correct: correctIndex,
        explanation: ex.explanation,
      };
    });
  };

  // Helper function to get final answer from answer data (same as TAGEFullTest.js)
  const getFinalAnswerIndex = (answerData) => {
    if (!answerData) return null;

    // Check if cancelled (all 10 bars filled)
    const allTopBarsFilled =
      answerData.topBars && answerData.topBars.length === 5;
    const allBottomBarsFilled =
      answerData.bottomBars && answerData.bottomBars.length === 5;
    if (allTopBarsFilled && allBottomBarsFilled) {
      return null; // Cancelled
    }

    // If bottom bars exist, use first bottom bar (modification)
    if (answerData.bottomBars && answerData.bottomBars.length > 0) {
      const letter = answerData.bottomBars[0];
      // Convert letter to index (A=0, B=1, etc.)
      return typeof letter === 'string' ? letter.charCodeAt(0) - 65 : letter;
    }

    // Otherwise use first top bar (initial answer)
    if (answerData.topBars && answerData.topBars.length > 0) {
      const letter = answerData.topBars[0];
      // Convert letter to index (A=0, B=1, etc.)
      return typeof letter === 'string' ? letter.charCodeAt(0) - 65 : letter;
    }

    return null;
  };

  // Handle answer change from answer sheet (same format as TAGEFullTest.js)
  const handleAnswerChange = (questionNum, answerData) => {
    // questionNum is 1-based, convert to 0-based for selectedAnswers
    const questionIndex = questionNum - 1;
    setSelectedAnswers({
      ...selectedAnswers,
      [questionIndex]: answerData,
    });
  };

  const handleNextQuestion = () => {
    const questions = getFilteredQuestions();
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      if (practiceMode === 'timed') startTimer();
    } else {
      finishPractice();
    }
  };

  const handlePrevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const finishPractice = async () => {
    setIsTimerActive(false);
    setShowResults(true);
    const questions = getFilteredQuestions();
    const correct = questions.reduce((acc, question, index) => {
      const userAnswerIndex = getFinalAnswerIndex(selectedAnswers[index]);
      return acc + (userAnswerIndex === question.correct ? 1 : 0);
    }, 0);
    const score = Math.round((correct / questions.length) * 100);

    // Lesson is completed if all questions have been answered
    const answeredQuestionsCount = Object.keys(selectedAnswers).length;
    const isLessonCompleted = answeredQuestionsCount === questions.length;
    const newStatus = isLessonCompleted ? 'completed' : 'in_progress';

    // Prepare exercise results
    const exerciseResults = Object.keys(selectedAnswers).map((index) => {
      const userAnswerIndex = getFinalAnswerIndex(selectedAnswers[index]);
      return {
        exerciseIndex: parseInt(index),
        isCorrect: userAnswerIndex === questions[index].correct,
        userAnswer: userAnswerIndex,
        correctAnswer: questions[index].correct,
      };
    });

    // Step 1: Save progress to localStorage FIRST
    const progress = {
      status: newStatus,
      currentExercise: questions.length,
      exerciseResults: exerciseResults,
      completedExercises: questions.length,
      totalExercises: questions.length,
      lastUpdated: new Date().toISOString(),
      score: score,
    };
    setScopedLocalStorageItem(
      `tage_lesson_progress_${lessonId}`,
      JSON.stringify(progress)
    );
    console.log('✅ Progress saved to localStorage first');

    // Step 2: Update completed lessons in localStorage if needed
    if (isLessonCompleted) {
      const completedLessons = JSON.parse(
        getScopedLocalStorageItem('tage_completed_lessons') || '[]'
      );
      if (!completedLessons.includes(lessonId)) {
        completedLessons.push(lessonId);
        setScopedLocalStorageItem(
          'tage_completed_lessons',
          JSON.stringify(completedLessons)
        );
      }

      // Update daily progress
      const today = new Date().toISOString().split('T')[0];
      const currentProgress = JSON.parse(
        getScopedLocalStorageItem(`tage_daily_progress_${today}`) ||
          '{"completedLessons": 0, "lessonIds": []}'
      );

      if (!currentProgress.lessonIds.includes(lessonId)) {
        currentProgress.completedLessons += 1;
        currentProgress.lessonIds.push(lessonId);
        setScopedLocalStorageItem(
          `tage_daily_progress_${today}`,
          JSON.stringify(currentProgress)
        );
      }
    }

    // Step 3: Sync to backend (non-blocking, in background)
    syncProgressToBackend(lessonId, progress, lesson)
      .then(() => {
        // After syncing progress, save individual exercise attempts
        for (let i = 0; i < questions.length; i++) {
          if (selectedAnswers[i] !== undefined) {
            const userAnswerIndex = getFinalAnswerIndex(selectedAnswers[i]);
            tageApi
              .saveExerciseAttempt({
                exercise_id: questions[i].id || i,
                user_answer: { answer: userAnswerIndex },
                is_correct: userAnswerIndex === questions[i].correct,
                time_taken_seconds: 120, // Default, can be improved with actual timing
              })
              .catch((error) => {
                console.warn('⚠️ Failed to save exercise attempt:', error);
              });
          }
        }
      })
      .catch((error) => {
        console.warn('⚠️ Failed to sync progress to backend:', error);
      });
  };

  const startPractice = (mode) => {
    // Check authentication before starting practice
    if (!isTokenValid()) {
      toastdisplay(
        'Session expirée. Veuillez vous reconnecter.',
        false,
        3000,
        setToast
      );

      // Logout and redirect to login after delay
      setTimeout(async () => {
        await logout();
      }, 3000);
      return;
    }

    setPracticeMode(mode);
    setShowPractice(true);
    setCurrentQuestion(0);
    setSelectedAnswers({});
    setShowResults(false);
    setPracticeStartTime(Date.now());
    if (mode === 'timed') startTimer();
  };

  const handleExitPractice = () => {
    if (practiceMode === 'timed' && isTimerActive && !showResults) {
      setShowExitConfirmation(true);
    } else {
      confirmExitPractice();
    }
  };

  const confirmExitPractice = () => {
    setIsTimerActive(false);
    setShowPractice(false);
    setCurrentQuestion(0);
    setSelectedAnswers({});
    setTimeLeft(0);
    setShowResults(false);
    setShowExitConfirmation(false);
  };

  const cancelExitPractice = () => {
    setShowExitConfirmation(false);
  };

  const renderResults = () => {
    const questions = getFilteredQuestions();
    const correct = questions.reduce((acc, question, index) => {
      const userAnswerIndex = getFinalAnswerIndex(selectedAnswers[index]);
      return acc + (userAnswerIndex === question.correct ? 1 : 0);
    }, 0);
    const score = Math.round((correct / questions.length) * 100);
    const isSuccess = score >= 70;

    return (
      <div className="tagelesson-results-container">
        <h2 className="tagelesson-results-title">Résultats de la Pratique</h2>

        <div
          className={`tagelesson-results-summary ${isSuccess ? 'success' : 'needs-practice'}`}
        >
          <h3
            className={`tagelesson-results-summary-title ${isSuccess ? 'success' : 'needs-practice'}`}
          >
            {isSuccess
              ? '🎉 Excellent travail !'
              : '📚 Continuez à pratiquer !'}
          </h3>
          <p className="tagelesson-results-score">
            {correct}/{questions.length} correct ({score}%)
          </p>
          <p className="tagelesson-results-message">
            {score >= 90
              ? 'Excellente maîtrise !'
              : score >= 70
                ? 'Bonne compréhension !'
                : 'Revoyez les concepts et réessayez.'}
          </p>
        </div>

        {questions.map((question, index) => {
          const userAnswerIndex = getFinalAnswerIndex(selectedAnswers[index]);
          const isCorrect = userAnswerIndex === question.correct;
          return (
            <div
              key={index}
              className={`tagelesson-result-item ${isCorrect ? 'correct' : 'incorrect'}`}
            >
              <p className="tagelesson-result-question">
                {index + 1}. {renderMathText(question.question)}
              </p>
              {question.diagram && (
                <div className="tagelesson-result-diagram">
                  <DiagramRenderer diagram={question.diagram} />
                </div>
              )}
              <p className="tagelesson-result-answer">
                <strong>Votre réponse:</strong>{' '}
                {userAnswerIndex !== null
                  ? renderMathText(question.options[userAnswerIndex])
                  : 'Pas de réponse'}
              </p>
              <p className="tagelesson-result-answer">
                <strong>Réponse correcte:</strong>{' '}
                {renderMathText(question.options[question.correct])}
              </p>
              <p className="tagelesson-result-explanation">
                {renderMathText(question.explanation)}
              </p>
            </div>
          );
        })}

        <div className="tagelesson-results-actions">
          <button
            onClick={() => setShowPractice(false)}
            className="tagelesson-btn-primary"
          >
            Retour à la Leçon
          </button>
          <button
            onClick={() => {
              setShowResults(false);
              setCurrentQuestion(0);
              setSelectedAnswers({});
              if (practiceMode === 'timed') startTimer();
            }}
            className="tagelesson-btn-secondary"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  };

  const renderPractice = () => {
    if (showResults) return renderResults();

    const questions = getFilteredQuestions();

    // Safety check
    if (!questions || questions.length === 0) {
      return (
        <div className="tagelesson-practice-container">
          <div className="tagelesson-error">
            Aucune question disponible pour la pratique.
          </div>
        </div>
      );
    }

    const question = questions[currentQuestion];
    if (!question || !question.options) {
      return (
        <div className="tagelesson-practice-container">
          <div className="tagelesson-error">
            Question non trouvée ou invalide.
          </div>
        </div>
      );
    }

    // Convert selectedAnswers from 0-based to 1-based for answer sheet
    // selectedAnswers now stores the full answer object { topBars: [], bottomBars: [] }
    const answersForSheet = {};
    Object.keys(selectedAnswers).forEach((questionIndex) => {
      const answerData = selectedAnswers[questionIndex];
      if (answerData) {
        // questionIndex is 0-based, but answer sheet uses 1-based numbering
        answersForSheet[parseInt(questionIndex) + 1] = answerData;
      }
    });

    return (
      <div className="tagelesson-practice-container">
        <div className="tagelesson-progress-bar">
          <div
            className="tagelesson-progress-fill"
            style={{
              width: `${((currentQuestion + 1) / questions.length) * 100}%`,
            }}
          />
        </div>

        <div className="tagelesson-practice-content-wrapper">
          {/* Left Panel: Question */}
          <div className="tagelesson-practice-question-panel">
            <div className="tagelesson-practice-question-badge">
              Question {currentQuestion + 1}
            </div>

            <div className="tagelesson-practice-question-content">
              <h3 className="tagelesson-practice-question-text">
                {renderMathText(question.question)}
              </h3>

              {/* Render diagram if present */}
              {question.diagram && (
                <div className="tagelesson-practice-question-diagram">
                  <DiagramRenderer diagram={question.diagram} />
                </div>
              )}

              <div className="tagelesson-practice-options-display">
                {question.options.map((option, index) => (
                  <div key={index} className="tagelesson-practice-option-item">
                    {renderMathText(option)}
                  </div>
                ))}
              </div>
            </div>

            <div className="tagelesson-practice-navigation">
              <button
                onClick={handleExitPractice}
                className="tagelesson-btn-secondary"
              >
                <FiArrowLeft size={16} />
                Retour à la Leçon
              </button>

              <div className="tagelesson-practice-nav-info">
                {currentQuestion + 1} / {questions.length}
              </div>

              {selectedAnswers[currentQuestion] !== undefined && (
                <button
                  onClick={handleNextQuestion}
                  className="tagelesson-btn-primary"
                >
                  {currentQuestion === questions.length - 1 ? (
                    <>
                      <FiCheck size={16} />
                      Terminer
                    </>
                  ) : (
                    <>
                      Suivant
                      <FiArrowLeft
                        size={16}
                        style={{ transform: 'rotate(180deg)' }}
                      />
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Right Panel: Answer Sheet */}
          <div className="tagelesson-practice-answer-panel">
            <div className="tagelesson-practice-answer-panel-header">
              <h3>Feuille de Réponses</h3>
              <p className="tagelesson-practice-answer-hint">
                Cliquez sur une case pour sélectionner votre réponse
              </p>
            </div>
            <TAGEAnswerSheet
              answers={answersForSheet}
              onAnswerChange={handleAnswerChange}
              currentQuestion={currentQuestion + 1}
              subTests={[
                {
                  id: 1,
                  name: 'Pratique',
                  start: 1,
                  end: questions.length,
                },
              ]}
              onQuestionClick={(questionNum) => {
                setCurrentQuestion(questionNum - 1);
              }}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderQuestionPalette = () => {
    if (!showQuestionPalette) return null;
    const questions = getFilteredQuestions();

    return (
      <div
        className="tagelesson-palette-overlay"
        onClick={() => setShowQuestionPalette(false)}
      >
        <div
          className="tagelesson-palette-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="tagelesson-palette-header">
            <h3>Toutes les questions</h3>
            <button
              onClick={() => setShowQuestionPalette(false)}
              className="tagelesson-palette-close"
            >
              <FiX />
            </button>
          </div>
          <div className="tagelesson-palette-grid">
            {questions.map((q, index) => (
              <button
                key={index}
                className={`tagelesson-palette-item ${
                  selectedAnswers[index] !== undefined ? 'answered' : ''
                } ${currentQuestion === index ? 'current' : ''}`}
                onClick={() => {
                  setCurrentQuestion(index);
                  setShowQuestionPalette(false);
                }}
              >
                {index + 1}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderExitConfirmationModal = () => {
    if (!showExitConfirmation) return null;

    return (
      <div className="tagelesson-modal-overlay">
        <div className="tagelesson-modal-content">
          <div className="tagelesson-modal-icon">⚠️</div>

          <h3 className="tagelesson-modal-title">
            Quitter la Pratique Chronométrée ?
          </h3>

          <p className="tagelesson-modal-text">
            Êtes-vous sûr de vouloir quitter ? Votre progression dans cette
            session chronométrée sera perdue.
          </p>

          <div className="tagelesson-modal-actions">
            <button
              onClick={cancelExitPractice}
              className="tagelesson-btn-continue"
            >
              Continuer la Pratique
            </button>

            <button
              onClick={confirmExitPractice}
              className="tagelesson-btn-exit"
            >
              Quitter
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (showPractice) {
    return (
      <div className="layout">
        <Sidebar />
        <div className="content">
          <div className="tagelesson-practice-container">
            <div className="tagelesson-practice-header">
              <div className="tagelesson-practice-header-left">
                <h2 className="tagelesson-practice-title">
                  {practiceMode === 'timed'
                    ? 'Pratique Chronométrée'
                    : 'Pratique Libre'}
                </h2>
                {practiceMode === 'timed' && (
                  <div className="tagelesson-timer">
                    <FiClock size={16} />
                    <span>
                      {Math.floor(timeLeft / 60)}:
                      {String(timeLeft % 60).padStart(2, '0')}
                    </span>
                  </div>
                )}
              </div>

              <div className="tagelesson-practice-header-right">
                <button
                  className="tagelesson-practice-nav-btn"
                  onClick={handlePrevQuestion}
                  disabled={currentQuestion === 0}
                >
                  <FiChevronLeft />
                </button>
                <button
                  className="tagelesson-practice-nav-btn"
                  onClick={() => setShowQuestionPalette(true)}
                >
                  <FiGrid />
                </button>
                <button
                  className="tagelesson-practice-nav-btn"
                  onClick={handleNextQuestion}
                  disabled={
                    currentQuestion === getFilteredQuestions().length - 1
                  }
                >
                  <FiChevronRight />
                </button>
                <button
                  onClick={handleExitPractice}
                  className="tagelesson-practice-exit-btn"
                >
                  <FiX />
                </button>
              </div>
            </div>

            {renderPractice()}
          </div>
          {renderExitConfirmationModal()}
          {renderQuestionPalette()}
        </div>
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="layout">
        <Sidebar />
        <div className="content">
          <div className="tagelesson-loading">
            <div className="tagelesson-loading-content">
              <div className="tagelesson-spinner" />
              <p>Chargement de la leçon...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Parse lesson content into sections based on H3 tags
  const getContentSections = (subcontent) => {
    if (!subcontent || subcontent.length === 0) {
      return [
        {
          id: 'content',
          title: 'Contenu du Cours',
          content: '<p>Aucun contenu disponible</p>',
        },
      ];
    }

    return subcontent.map((item, index) => ({
      id: `section-${index}`,
      title: item.title,
      content: `<div>${item.lesson}</div>`,
    }));
  };

  // Extract key concepts for quick reference
  const extractKeyConcepts = (htmlContent) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    const concepts = [];

    // Extract from strong tags
    const strongElements = doc.querySelectorAll('strong');
    strongElements.forEach((el) => {
      const text = el.textContent.trim();
      if (text && text.length < 100 && !text.includes(':')) {
        concepts.push(text);
      }
    });

    // Extract from H4 headings only (H3 are now section titles)
    const headings = doc.querySelectorAll('h4');
    headings.forEach((heading) => {
      const text = heading.textContent.trim();
      if (text && text.length < 100) {
        concepts.push(text);
      }
    });

    // Extract from list items that start with a strong tag
    const listItems = doc.querySelectorAll('li');
    listItems.forEach((li) => {
      const firstStrong = li.querySelector('strong');
      if (firstStrong) {
        const text = firstStrong.textContent.trim();
        if (text && text.length < 80 && !text.includes(':')) {
          concepts.push(text);
        }
      }
    });

    // Remove duplicates and limit to 8 key concepts
    return [...new Set(concepts)].slice(0, 8);
  };

  const contentSections = lesson ? getContentSections(lesson.subcontent) : [];

  // Extract key concepts from all subcontent lessons
  const getAllLessonsHtml = (subcontent) => {
    if (!subcontent) return '';
    return subcontent.map((item) => item.lesson).join('');
  };

  // Show loading state
  if (loading) {
    return (
      <div className="layout">
        <Sidebar />
        <div className="content">
          <div className="tagelesson-container">
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '400px',
                gap: '24px',
              }}
            >
              <div
                className="spinner"
                style={{
                  width: '60px',
                  height: '60px',
                  borderWidth: '6px',
                }}
              ></div>
              <div style={{ textAlign: 'center' }}>
                <h2 style={{ margin: '0 0 12px 0', fontSize: '24px' }}>
                  Chargement de la leçon...
                </h2>
                <p
                  style={{
                    margin: 0,
                    fontSize: '16px',
                    color: 'var(--liwaza-on-surface-variant)',
                  }}
                >
                  Veuillez patienter
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="layout">
        <Sidebar />
        <div className="content">
          <div className="tagelesson-container">
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '400px',
                gap: '24px',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: 'var(--liwaza-error-container)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '40px',
                }}
              >
                ⚠️
              </div>
              <div>
                <h2
                  style={{
                    margin: '0 0 12px 0',
                    fontSize: '24px',
                    color: 'var(--liwaza-error)',
                  }}
                >
                  Erreur de chargement
                </h2>
                <p
                  style={{
                    margin: '0 0 24px 0',
                    fontSize: '16px',
                    color: 'var(--liwaza-on-surface-variant)',
                  }}
                >
                  {getErrorMessage(error, "Impossible de charger la leçon.")}
                </p>
                <div
                  style={{
                    display: 'flex',
                    gap: '12px',
                    justifyContent: 'center',
                  }}
                >
                  <button
                    className="btn-lwz-primary"
                    onClick={() => loadLesson()}
                  >
                    Réessayer
                  </button>
                  <button
                    className="btn-lwz-outline"
                    onClick={() => navigate('/exams/tage')}
                  >
                    Retour au TAGE MAGE
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show not found state
  if (!lesson) {
    return (
      <div className="layout">
        <Sidebar />
        <div className="content">
          <div className="tagelesson-container">
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '400px',
                gap: '24px',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  fontSize: '80px',
                }}
              >
                📚
              </div>
              <div>
                <h2
                  style={{
                    margin: '0 0 12px 0',
                    fontSize: '24px',
                  }}
                >
                  Leçon non trouvée
                </h2>
                <p
                  style={{
                    margin: '0 0 24px 0',
                    fontSize: '16px',
                    color: 'var(--liwaza-on-surface-variant)',
                  }}
                >
                  La leçon demandée n'existe pas ou n'est plus disponible.
                </p>
                <button
                  className="btn-lwz-primary"
                  onClick={() => navigate('/exams/tage')}
                >
                  Retour au TAGE MAGE
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const keyConcepts = lesson
    ? extractKeyConcepts(getAllLessonsHtml(lesson.subcontent))
    : [];

  return (
    <div className="layout">
      <Sidebar />
      {toast.show && (
        <div className="toast-notification">
          <FiCheck />
          <span>{toast.message}</span>
        </div>
      )}
      <div className="content">
        <div className="tagelesson-container">
          {/* Header */}
          <div className="tagelesson-header">
            <button
              onClick={() => navigate('/exams/tage')}
              className="tagelesson-back-btn"
            >
              <FiArrowLeft /> Retour au TAGE MAGE
            </button>

            <h1 className="tagelesson-title">
              <FiFileText />
              {lesson.title}
            </h1>
            <div
              className="tagelesson-description"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(lesson.introduction) }}
            />
            <div className="tagelesson-meta">
              <span className="tagelesson-meta-item">
                <FiClock /> {Math.round(lesson.work_time_hours * 60)} min
              </span>
              <span className="tagelesson-meta-item">
                <FiTarget /> {lesson.exercises?.length || 0} exercices
              </span>
            </div>
          </div>

          {/* Section Navigation */}
          <div className="tagelesson-section-nav">
            {contentSections.map((section, index) => (
              <button
                key={section.id}
                onClick={() => setCurrentSection(index)}
                className={`tagelesson-section-btn ${currentSection === index ? 'active' : ''}`}
              >
                {section.title}
              </button>
            ))}
          </div>

          {/* Main Content */}
          <div className="tagelesson-content-grid">
            {/* Content Area */}
            <div>
              <div className="tagelesson-content-area">
                <div
                  ref={contentRef}
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(contentSections[currentSection].content),
                  }}
                />
              </div>
            </div>

            {/* Sidebar */}
            <div className="tagelesson-sidebar">
              <h3 className="tagelesson-sidebar-title">
                <FiBookOpen />
                Pratique
              </h3>

              <div className="tagelesson-practice-section">
                <button
                  onClick={() => startPractice('untimed')}
                  className="tagelesson-practice-btn untimed"
                >
                  <FiBookOpen />
                  Mode Pratique
                </button>
                <p className="tagelesson-practice-info">
                  {lesson.exercises?.length || 0} questions, sans limite de
                  temps
                </p>
              </div>

              <div className="tagelesson-practice-section">
                <button
                  onClick={() => startPractice('timed')}
                  className="tagelesson-practice-btn timed"
                >
                  <FiClock />
                  Pratique Chronométrée
                </button>
                <p className="tagelesson-practice-info">
                  {lesson.exercises?.length || 0} questions, 2 min par question
                </p>
              </div>

              {/* Get Started With Component */}
              <div className="tagelesson-help-section">
                <GetStartedWith
                  title="Besoin d'aide sur cette leçon ?"
                  spaceName="tagemage"
                  humanCoachTitle="Parler à un coach TAGE MAGE"
                  liwazaAITitle="Demander à l'IA"
                  communityTitle="Demander aux autres étudiants"
                  showHumanCoach={true}
                  showLiwazaAI={true}
                  showCommunity={true}
                  aifirstmessage={`J'ai besoin d'aide pour comprendre ${lesson.title}`}
                  aicontext={lesson.introduction}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Notebook Confirmation */}
        {showNotebookConfirmation && (
          <div className="tagelesson-notebook-toast">
            <FiCheck size={20} />
            <span className="tagelesson-toast-text">
              Récapitulatif enregistré dans votre livre de notes !
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default TAGELesson;
